/*
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  ASSET: Listicle Reason Formula Carousel
  Purpose: Swipeable ingredient carousel for Reason 03, with touch swipe on phones and tablets and click-and-drag with a mouse. Shows one dash per scroll stop (4 on desktop with two cards visible, 5 on mobile), highlights the current one and scrolls to a card when its dash is clicked.
  Last updated: October 2026
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
*/

(() => {
  if (window.elListicleFormula) return;
  window.elListicleFormula = true;

  const initCarousel = (carousel) => {
    if (carousel.dataset.elFormulaReady) return;
    carousel.dataset.elFormulaReady = 'true';

    const track = carousel.querySelector('[data-el-formula-track]');
    const slides = Array.from(carousel.querySelectorAll('[data-el-formula-slide]'));
    const dots = Array.from(carousel.querySelectorAll('[data-el-formula-dot]'));
    if (!track || slides.length === 0) return;

    let stops = [];

    const getStops = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const trackLeft = track.getBoundingClientRect().left - track.scrollLeft;
      const positions = [];
      slides.forEach((slide) => {
        const offset = Math.min(slide.getBoundingClientRect().left - trackLeft, maxScroll);
        if (!positions.some((position) => Math.abs(position - offset) < 2)) positions.push(offset);
      });
      return positions;
    };

    const setActive = () => {
      let activeIndex = 0;
      let closest = Infinity;
      stops.forEach((stop, index) => {
        const distance = Math.abs(track.scrollLeft - stop);
        if (distance < closest) {
          closest = distance;
          activeIndex = index;
        }
      });
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });
    };

    const refresh = () => {
      stops = getStops();
      dots.forEach((dot, index) => {
        dot.hidden = index >= stops.length;
      });
      setActive();
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        if (stops[index] === undefined) return;
        track.scrollTo({ left: stops[index], behavior: 'smooth' });
      });
    });

    const getNearestIndex = () => {
      let nearest = 0;
      stops.forEach((stop, index) => {
        if (Math.abs(track.scrollLeft - stop) < Math.abs(track.scrollLeft - stops[nearest])) nearest = index;
      });
      return nearest;
    };

    let drag = null;
    let releaseTimer = null;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || stops.length < 2) return;
      event.preventDefault();
      clearTimeout(releaseTimer);
      drag = { startX: event.clientX, startScroll: track.scrollLeft, startIndex: getNearestIndex(), moved: false };
    });

    track.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const distance = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(distance) < 5) return;
      if (!drag.moved) {
        drag.moved = true;
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
      }
      track.scrollLeft = drag.startScroll - distance;
    });

    const endDrag = (event) => {
      if (!drag) return;
      const { moved, startX, startIndex } = drag;
      drag = null;
      if (!moved) return;
      const distance = event.clientX - startX;
      let target = getNearestIndex();
      if (Math.abs(distance) > 40) {
        target = Math.max(0, Math.min(stops.length - 1, startIndex + (distance < 0 ? 1 : -1)));
      }
      track.scrollTo({ left: stops[target], behavior: 'smooth' });
      const release = () => {
        clearTimeout(releaseTimer);
        track.removeEventListener('scrollend', release);
        if (!drag) track.classList.remove('is-dragging');
      };
      track.addEventListener('scrollend', release);
      releaseTimer = setTimeout(release, 700);
      const blockClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      };
      track.addEventListener('click', blockClick, { capture: true, once: true });
      setTimeout(() => track.removeEventListener('click', blockClick, { capture: true }), 0);
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());

    let frame = null;
    track.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setActive();
      });
    }, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(refresh).observe(track);
    } else {
      window.addEventListener('resize', refresh);
    }

    refresh();
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-el-formula-carousel]').forEach(initCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
